import {Component, Input, ViewEncapsulation} from '@angular/core';

/**
 * Shared dark "Pebbl" dialog shell used inside NgbModal-hosted dialogs
 * (Add Business Address, Add Internal Note, …). Renders the panel chrome —
 * surface, border, radius, padding, title + optional help text — and projects
 * the dialog body/footer via <ng-content>.
 *
 * Encapsulation is None (like app-rep-avatar) so the two `:has()` rules below
 * can neutralise ng-bootstrap's default white `.modal-content`/`.modal-dialog`
 * chrome wherever this shell appears — no per-call-site windowClass needed.
 * Every class is `pebbl-`/`pm-`-prefixed to stay clear of global/bootstrap CSS.
 *
 * Usage:
 *   <app-pebbl-modal title="Add Business Address">
 *     <form …> … <div class="pm-foot"> … </div> </form>
 *   </app-pebbl-modal>
 *
 * A dialog can also place a compact action (an icon link/button) on the title
 * row by tagging it `pm-head-act`, which projects it into the head:
 *   <a class="pm-head-act" pm-head-act …><svg …/></a>
 */
@Component({
  selector: 'app-pebbl-modal',
  standalone: true,
  encapsulation: ViewEncapsulation.None,
  host: {
    class: 'pebbl-modal',
    // `title` is BOTH this component's input and a global HTML attribute, and a
    // STATIC `title="…"` in a caller's template is written to the DOM as well as
    // fed to the input. The host element then carried a real title, so resting
    // the pointer anywhere over the dialog raised the browser's own tooltip
    // showing the heading again (seen on the linked-records dialog). Strip the
    // attribute here so no caller can reintroduce it — the heading still renders
    // from the input below.
    '[attr.title]': 'null'
  },
  template: `
    <div class="pm-head">
      <h3 class="pm-title">{{ title }}</h3>
      @if (subtitle) {
        <p class="pm-subtitle">{{ subtitle }}</p>
      }
      <!-- A subtitle that needs MARKUP (the record quick view bolds the record
           number) cannot come through the plain-text \`subtitle\` input, so it is
           projected here instead of through the default slot below. Inside the
           head is where it has to land: console-v2 nests its \`.rqp-sub\` in
           \`.rqp-head\` and pins the head as one box, and a subtitle projected as
           a SIBLING of the head could not be pinned with it - on desktop the
           title stuck and the subtitle scrolled away under it. -->
      <ng-content select="[pm-head-sub]"></ng-content>
      <ng-content select="[pm-head-act]"></ng-content>
    </div>
    <ng-content></ng-content>
  `,
  styleUrl: './pebbl-modal.component.css'
})
export class PebblModalComponent {
  @Input() title = '';
  @Input() subtitle = '';
}
